import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { T } from "./adminTheme";
import {
  Refresh,
  CleaningServices,
  PersonOff,
  PrecisionManufacturing,
  WarningAmber,
  LockOpen,
  Build,
  BugReport,
  ErrorOutline,
} from "@mui/icons-material";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  }).format(date);
};

const getStateColor = (state) => {
  switch (state) {
    case "OPEN_SHIFT_ONLY":             return "warning";
    case "GENERAL_IDLE_ONGOING":        return "info";
    case "JOB_STARTED_NO_OPERATION":    return "secondary";
    case "JOB_AND_PRODUCTION_ONGOING":  return "error";
    case "JOB_AND_IDLE_ONGOING":        return "warning";
    default:                            return "default";
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SectionHeading({ label, t }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
      <Box sx={{ width: 3, height: 14, borderRadius: "3px", background: t.green, opacity: 0.7 }} />
      <Typography sx={{
        fontSize: 10, fontWeight: 700, letterSpacing: "0.10em",
        textTransform: "uppercase", color: t.muted,
      }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "0.5px", background: t.border }} />
    </Box>
  );
}

function MiniStatCard({ label, value, color, icon, t, isDark }) {
  return (
    <Box sx={{
      borderRadius: "12px", p: "12px 14px",
      background: t.s1, border: `0.5px solid ${t.border}`,
      borderTop: `2px solid ${color}`,
      display: "flex", alignItems: "center", gap: 1.5,
      boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4)" : "none",
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: "9px", flexShrink: 0,
        background: `${color}15`, border: `0.5px solid ${color}25`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, boxShadow: isDark ? `0 0 10px ${color}12` : "none",
      }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: t.heading, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
          {value ?? "—"}
        </Typography>
        <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 500, mt: 0.2 }}>{label}</Typography>
      </Box>
    </Box>
  );
}

function ItemCard({ children, accent, t, isDark, checked }) {
  return (
    <Box sx={{
      borderRadius: "10px",
      border: `0.5px solid ${accent ? accent + "35" : t.border}`,
      borderLeft: accent ? `2.5px solid ${accent}` : `0.5px solid ${t.border}`,
      background: checked
        ? (isDark ? "rgba(59,130,246,0.05)" : "rgba(37,99,235,0.03)")
        : t.s1,
      p: 2,
      transition: "background 0.12s ease",
    }}>
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const ManhourSessionRecoveryPanel = ({
  currentAdminId,
  initialFilter = null,
  onInitialFilterApplied,
}) => {
  const theme  = useTheme();
  const t      = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState({ supervisorId: "", stationCode: "", empId: "" });
  const [activeData, setActiveData] = useState([]);
  const [stuckData, setStuckData] = useState({
    stuckEmployeeCount: 0, stuckMachineCount: 0, orphanOpenDetailCount: 0,
    stuckEmployees: [], stuckMachines: [], orphanOpenDetails: [],
  });
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingStuck,  setLoadingStuck]  = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedEmployees,  setSelectedEmployees]  = useState([]);
  const [selectedMachines,   setSelectedMachines]   = useState([]);
  const [selectedDetailIds,  setSelectedDetailIds]  = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", actionType: "", payload: null });
  const [reason,   setReason]   = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const adminId = currentAdminId || localStorage.getItem("username") || "";
  const showMessage = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const appliedInitialFilterKeyRef = useRef("");

  // ── Build URL helper (now accepts optional sourceFilters parameter) ─────────
  const buildActiveStatusUrl = useCallback((sourceFilters = filters) => {
    const params = new URLSearchParams();
    const supervisorId = String(sourceFilters.supervisorId || "").trim();
    const stationCode = String(sourceFilters.stationCode || "").trim().toUpperCase();
    const empId = String(sourceFilters.empId || "").trim();

    if (supervisorId) params.append("supervisorId", supervisorId);
    if (stationCode)  params.append("stationCode", stationCode);
    if (empId)        params.append("empId", empId);

    const query = params.toString();
    return `${API_BASE_URL}/admin/manhour-session-recovery/active-status${query ? `?${query}` : ""}`;
  }, [filters]);

  // ── Fetch active status (now accepts optional overrideFilters and options) ──
  const fetchActiveStatus = useCallback(async (overrideFilters = null, options = {}) => {
    const sourceFilters = overrideFilters || filters;
    const { silent = false } = options;

    setLoadingActive(true);
    try {
      const res = await axios.get(buildActiveStatusUrl(sourceFilters));
      setActiveData(res.data?.data || []);

      if (!silent) {
        showMessage("Active man-hour sessions loaded.");
      }
    } catch (err) {
      showMessage(
        err?.response?.data?.sqlMessage ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to load active sessions.",
        "error"
      );
    } finally {
      setLoadingActive(false);
    }
  }, [buildActiveStatusUrl, filters]);

  

  useEffect(() => {
  if (!initialFilter) return;

  // Prevent duplicate applications
  const filterKey = JSON.stringify(initialFilter);
  if (appliedInitialFilterKeyRef.current === filterKey) return;
  appliedInitialFilterKeyRef.current = filterKey;

  const nextFilters = {
    supervisorId: initialFilter.supervisorId ? String(initialFilter.supervisorId) : "",
    stationCode: initialFilter.stationCode ? String(initialFilter.stationCode).toUpperCase() : "",
    empId: initialFilter.employeeId
      ? String(initialFilter.employeeId)
      : initialFilter.empId
        ? String(initialFilter.empId)
        : "",
  };

  setTab(0);
  setFilters(nextFilters);
  fetchActiveStatus(nextFilters, { silent: true });

  if (typeof onInitialFilterApplied === "function") {
    onInitialFilterApplied();
  }
}, [initialFilter, onInitialFilterApplied, fetchActiveStatus ]);
// ☝️ Notice: fetchActiveStatus is NOT in dependencies!

  const fetchStuckAssets = async () => {
    setLoadingStuck(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/manhour-session-recovery/stuck-assets`);
      setStuckData({
        stuckEmployeeCount:    res.data?.stuckEmployeeCount    || 0,
        stuckMachineCount:     res.data?.stuckMachineCount     || 0,
        orphanOpenDetailCount: res.data?.orphanOpenDetailCount || 0,
        stuckEmployees:        res.data?.stuckEmployees        || [],
        stuckMachines:         res.data?.stuckMachines         || [],
        orphanOpenDetails:     res.data?.orphanOpenDetails     || [],
      });
      showMessage("Stuck assets loaded.");
    } catch (err) {
      showMessage(err?.response?.data?.error || "Failed to load stuck assets.", "error");
    } finally { setLoadingStuck(false); }
  };

  const openConfirm = (opts) => { setReason(""); setConfirmDialog({ open: true, ...opts }); };
  const closeConfirm = () => {
    if (actionLoading) return;
    setConfirmDialog({ open: false, title: "", message: "", actionType: "", payload: null });
    setReason("");
  };

  const cleanupEmployee     = (item) => openConfirm({ title: "Cleanup Employee Session", message: `This will close the open man-hour session for ${item.empName} (${item.empId}) on workstation ${item.stationCode}.`, actionType: "CLEANUP_EMPLOYEE", payload: item });
  const cleanupWorkstation  = () => {
    const stationCode  = filters.stationCode.trim().toUpperCase();
    const supervisorId = filters.supervisorId.trim();
    if (!stationCode || !supervisorId) { showMessage("Please enter Supervisor ID and Station Code.", "warning"); return; }
    openConfirm({ title: "Cleanup Full Workstation", message: `Cleanup all open man-hour sessions for workstation ${stationCode}.`, actionType: "CLEANUP_WORKSTATION", payload: { supervisorId, stationCode } });
  };
  const releaseSelectedStuckAssets = () => {
    if (!selectedEmployees.length && !selectedMachines.length) { showMessage("Please select stuck employees or machines.", "warning"); return; }
    openConfirm({ title: "Release Stuck Assets", message: `Release ${selectedEmployees.length} employee(s) and ${selectedMachines.length} machine(s).`, actionType: "RELEASE_STUCK_ASSETS", payload: { employeeIds: selectedEmployees, machineCodes: selectedMachines } });
  };
  const fixSelectedOpenDetails = () => {
    if (!selectedDetailIds.length) { showMessage("Please select open detail rows to fix.", "warning"); return; }
    openConfirm({ title: "Fix Open Detail Rows", message: `Close ${selectedDetailIds.length} open detail row(s) using the recommended end time.`, actionType: "FIX_OPEN_DETAILS", payload: { detailLogIds: selectedDetailIds } });
  };



const handleConfirmAction = async () => {
  setActionLoading(true);

  try {
    const { actionType, payload, message } = confirmDialog;
    const finalReason = reason.trim() || message || "Manual man-hour recovery action";

    if (actionType === "CLEANUP_EMPLOYEE") {
      await axios.post(`${API_BASE_URL}/admin/manhour-session-recovery/cleanup-employee`, {
        supervisorId: payload.supervisorId,
        stationCode: payload.stationCode,
        empId: payload.empId,
        cleanedBy: adminId,
        reason: finalReason,
      });

      showMessage("Employee cleanup completed.");

      await fetchActiveStatus(
        {
          supervisorId: payload.supervisorId || filters.supervisorId,
          stationCode: payload.stationCode || filters.stationCode,
          empId: "",
        },
        { silent: true }
      );

      await fetchStuckAssets();
    }

    if (actionType === "CLEANUP_WORKSTATION") {
      await axios.post(`${API_BASE_URL}/admin/manhour-session-recovery/cleanup-workstation`, {
        supervisorId: payload.supervisorId,
        stationCode: payload.stationCode,
        cleanedBy: adminId,
        reason: finalReason,
      });

      showMessage("Workstation cleanup completed.");

      await fetchActiveStatus(
        {
          supervisorId: payload.supervisorId,
          stationCode: payload.stationCode,
          empId: "",
        },
        { silent: true }
      );

      await fetchStuckAssets();
    }

    if (actionType === "RELEASE_STUCK_ASSETS") {
      await axios.post(`${API_BASE_URL}/admin/manhour-session-recovery/release-stuck-assets`, {
        employeeIds: payload.employeeIds,
        machineCodes: payload.machineCodes,
        cleanedBy: adminId,
        reason: finalReason,
      });

      setSelectedEmployees([]);
      setSelectedMachines([]);
      showMessage("Stuck assets released.");
      await fetchStuckAssets();
    }

    if (actionType === "FIX_OPEN_DETAILS") {
      await axios.post(`${API_BASE_URL}/admin/manhour-session-recovery/fix-open-details`, {
        detailLogIds: payload.detailLogIds,
        cleanedBy: adminId,
        reason: finalReason,
      });

      setSelectedDetailIds([]);
      showMessage("Open detail rows fixed.");
      await fetchStuckAssets();
    }

    setConfirmDialog({
      open: false,
      title: "",
      message: "",
      actionType: "",
      payload: null,
    });
    setReason("");
  } catch (err) {
    showMessage(
      err?.response?.data?.sqlMessage ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Recovery action failed.",
      "error"
    );
  } finally {
    setActionLoading(false);
  }
};


  const toggleSelectedEmployee = (id) => setSelectedEmployees((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleSelectedMachine  = (c)  => setSelectedMachines ((p) => p.includes(c)  ? p.filter((x) => x !== c)  : [...p, c]);
  const toggleSelectedDetail   = (id) => setSelectedDetailIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const totalStuckIssueCount = useMemo(() =>
    stuckData.stuckEmployeeCount + stuckData.stuckMachineCount + stuckData.orphanOpenDetailCount,
  [stuckData]);

  const cardSx = {
    borderRadius: "14px",
    border: `0.5px solid ${t.border}`,
    background: t.s0,
    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)" : "none",
    overflow: "hidden",
  };

  const dangerBtnSx = {
    fontWeight: 600, fontSize: 12,
    background: isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.10)",
    color: isDark ? "#f87171" : "#dc2626",
    border: `0.5px solid ${t.red}40`, boxShadow: "none",
    "&:hover": { background: isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)", boxShadow: "none" },
  };

  const amberBtnSx = {
    fontWeight: 600, fontSize: 13,
    background: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.10)",
    color: isDark ? "#fbbf24" : "#d97706",
    border: `0.5px solid ${t.amber}40`, boxShadow: "none",
    "&:hover": { background: isDark ? "rgba(245,158,11,0.22)" : "rgba(245,158,11,0.16)", boxShadow: "none" },
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Paper sx={{ ...cardSx, p: 0 }}>

        {/* Header */}
        <Box sx={{
          px: 3, py: 2,
          borderBottom: `0.5px solid ${t.border}`,
          display: "flex", alignItems: "center", gap: 1.5,
          background: isDark
            ? "linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(59,130,246,0.04) 50%, transparent 80%)"
            : "transparent",
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
            background: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.08)",
            border: `0.5px solid ${isDark ? "rgba(16,185,129,0.30)" : "rgba(16,185,129,0.20)"}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: t.green, boxShadow: isDark ? "0 0 12px rgba(16,185,129,0.15)" : "none",
          }}>
            <CleaningServices sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 400, color: t.heading, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
              Admin recovery for open man-hour sessions, stuck employees, locked machines, and open detail inconsistencies.
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{
            px: 2, borderBottom: `0.5px solid ${t.border}`,
            "& .MuiTab-root": { fontSize: 12, fontWeight: 600, textTransform: "none", color: t.secondary, minHeight: 42 },
            "& .Mui-selected": { color: t.blue },
            "& .MuiTabs-indicator": { backgroundColor: t.blue, height: 2 },
          }}
        >
          <Tab label="Active Sessions" />
          <Tab label="Stuck Assets / Inconsistencies" />
        </Tabs>

        {/* ── Tab 0: Active Sessions ─────────────────────────────────── */}
        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Filter bar */}
              <Box sx={{ p: 2, borderRadius: "12px", border: `0.5px solid ${t.border}`, background: t.s1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Supervisor ID" value={filters.supervisorId}
                      onChange={(e) => setFilters((p) => ({ ...p, supervisorId: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Station Code" value={filters.stationCode}
                      onChange={(e) => setFilters((p) => ({ ...p, stationCode: e.target.value.toUpperCase() }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Employee ID" value={filters.empId}
                      onChange={(e) => setFilters((p) => ({ ...p, empId: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" spacing={1}>
                      <Button fullWidth variant="contained"
                        startIcon={loadingActive ? <CircularProgress size={15} color="inherit" /> : <Refresh />}
                        onClick={() => fetchActiveStatus()} disabled={loadingActive}
                        sx={{ fontWeight: 600, fontSize: 13 }}
                      >
                        {loadingActive ? "Loading…" : "Search"}
                      </Button>
                      <Tooltip title="Cleanup full workstation (requires Supervisor ID + Station Code)">
                        <span>
                          <IconButton onClick={cleanupWorkstation} disabled={loadingActive}
                            sx={{ width: 36, height: 36, borderRadius: "8px", color: isDark ? "#f87171" : t.red, background: t.redAlpha, border: `0.5px solid ${t.red}35`, "&:hover": { background: `${t.red}20` } }}>
                            <PrecisionManufacturing sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Status row */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip icon={<WarningAmber sx={{ fontSize: 15 }} />}
                  label={`Active Sessions: ${activeData.length}`}
                  color={activeData.length > 0 ? "warning" : "success"} size="small" />
              </Stack>

              {loadingActive ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress size={26} sx={{ color: t.blue }} />
                  <Typography sx={{ mt: 1.5, fontSize: 12, color: t.muted, fontWeight: 500 }}>Loading active sessions…</Typography>
                </Box>
              ) : activeData.length === 0 ? (
                <Alert severity="success" sx={{ borderRadius: "10px" }}>No active man-hour sessions found for the selected filters.</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {activeData.map((item) => {
                    const isProduction = item.currentState === "JOB_AND_PRODUCTION_ONGOING";
                    const accent = isProduction ? t.red : t.green;
                    return (
                      <ItemCard key={item.shiftLogId} accent={accent} t={t} isDark={isDark}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                              <Typography sx={{ fontSize: 14, fontWeight: 700, color: t.heading, letterSpacing: "-0.01em" }}>
                                {item.empName}
                              </Typography>
                              <Chip size="small" label={`Emp: ${item.empId}`} />
                              <Chip size="small" label={item.currentState} color={getStateColor(item.currentState)} />
                              <Chip size="small" label={`Station: ${item.stationCode}`} color="primary" variant="outlined" />
                            </Stack>
                            <Typography sx={{ fontSize: 12, color: t.secondary, fontWeight: 500 }}>
                              Shift In: {formatDateTime(item.shiftInDatetime)}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: t.secondary }}>Supervisor: {item.supervisorId}</Typography>

                            <Box mt={1.5}>
                              <Typography sx={{ fontSize: 10, fontWeight: 700, color: t.muted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.75 }}>
                                Open Details
                              </Typography>
                              {item.openDetails.length === 0 ? (
                                <Typography sx={{ fontSize: 12, color: t.muted }}>No open detail rows — open shift only.</Typography>
                              ) : (
                                <Stack spacing={0.75}>
                                  {item.openDetails.map((detail) => (
                                    <Box key={detail.detailLogId} sx={{ p: "8px 12px", borderRadius: "8px", background: t.s2, border: `0.5px solid ${t.border}` }}>
                                      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" mb={0.4}>
                                        <Chip size="small" label={`ID: ${detail.detailLogId}`} />
                                        <Chip size="small" label={detail.operationCode || "JOB_STARTED"} color={detail.operationCode === "PROD" ? "error" : "default"} />
                                        <Typography sx={{ fontSize: 12, color: t.secondary }}>{detail.operationDescription}</Typography>
                                      </Stack>
                                      {detail.jobNumber && (
                                        <Typography sx={{ fontSize: 11, color: t.muted }}>Job: {detail.jobNumber} | {detail.customer || "—"}</Typography>
                                      )}
                                      <Typography sx={{ fontSize: 11, color: t.muted }}>Start: {formatDateTime(detail.startDatetime)}</Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              )}
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Stack spacing={1} alignItems="stretch">
                              <Button variant="contained" size="small"
                                startIcon={<CleaningServices sx={{ fontSize: 15 }} />}
                                onClick={() => cleanupEmployee(item)}
                                sx={dangerBtnSx}
                              >
                                Cleanup Session
                              </Button>
                            </Stack>
                          </Grid>
                        </Grid>
                      </ItemCard>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Box>
        )}

        {/* ── Tab 1: Stuck Assets ────────────────────────────────────── */}
        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Fetch stuck assets bar */}
              <Box sx={{ p: 2, borderRadius: "12px", border: `0.5px solid ${t.border}`, background: t.s1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="contained"
                    startIcon={loadingStuck ? <CircularProgress size={15} color="inherit" /> : <Refresh />}
                    onClick={fetchStuckAssets} disabled={loadingStuck}
                    sx={{ fontWeight: 600, fontSize: 13 }}
                  >
                    {loadingStuck ? "Loading…" : "Fetch Stuck Assets"}
                  </Button>
                  <Button variant="contained"
                    startIcon={<LockOpen />}
                    onClick={releaseSelectedStuckAssets}
                    disabled={!selectedEmployees.length && !selectedMachines.length}
                    sx={amberBtnSx}
                  >
                    Release Selected
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Chip icon={<BugReport sx={{ fontSize: 15 }} />}
                    label={`Total Issues: ${totalStuckIssueCount}`}
                    color={totalStuckIssueCount > 0 ? "error" : "success"} size="small" />
                </Stack>
              </Box>

              {loadingStuck ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress size={26} sx={{ color: t.blue }} />
                  <Typography sx={{ mt: 1.5, fontSize: 12, color: t.muted, fontWeight: 500 }}>Loading stuck assets…</Typography>
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {/* Mini stats */}
                  <Grid item xs={12} md={4}>
                    <MiniStatCard
                      label="Stuck Employees"
                      value={stuckData.stuckEmployeeCount}
                      color={t.red}
                      icon={<PersonOff sx={{ fontSize: 18 }} />}
                      t={t} isDark={isDark}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MiniStatCard
                      label="Stuck Machines"
                      value={stuckData.stuckMachineCount}
                      color={t.amber}
                      icon={<PrecisionManufacturing sx={{ fontSize: 18 }} />}
                      t={t} isDark={isDark}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <MiniStatCard
                      label="Open Inconsistencies"
                      value={stuckData.orphanOpenDetailCount}
                      color={t.red}
                      icon={<ErrorOutline sx={{ fontSize: 18 }} />}
                      t={t} isDark={isDark}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Stuck Employees */}
              <Box>
                <SectionHeading label="Stuck Employees" t={t} />
                {stuckData.stuckEmployees.length === 0 ? (
                  <Alert severity="success" sx={{ borderRadius: "10px" }}>No stuck employees found.</Alert>
                ) : (
                  <Stack spacing={1}>
                    {stuckData.stuckEmployees.map((emp) => {
                      const checked = selectedEmployees.includes(emp.Employee_ID);
                      return (
                        <ItemCard key={emp.Employee_ID} accent={checked ? t.red : null} t={t} isDark={isDark} checked={checked}>
                          <FormControlLabel
                            control={<Checkbox checked={checked} onChange={() => toggleSelectedEmployee(emp.Employee_ID)} size="small"
                              sx={{ color: t.muted, "&.Mui-checked": { color: t.red } }} />}
                            label={
                              <Box sx={{ ml: 0.5 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.heading }}>
                                  {emp.Name}{" "}
                                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: t.blue }}>({emp.Employee_ID})</span>
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: t.secondary, mt: 0.3 }}>
                                  Availability: {emp.avaliability} · Active Machine: {emp.activeMachineCode || "—"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2 }}>{emp.reason}</Typography>
                              </Box>
                            }
                          />
                        </ItemCard>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <Divider />

              {/* Stuck Machines */}
              <Box>
                <SectionHeading label="Stuck Machines" t={t} />
                {stuckData.stuckMachines.length === 0 ? (
                  <Alert severity="success" sx={{ borderRadius: "10px" }}>No stuck machines found.</Alert>
                ) : (
                  <Stack spacing={1}>
                    {stuckData.stuckMachines.map((machine) => {
                      const checked = selectedMachines.includes(machine.MachineCode);
                      return (
                        <ItemCard key={machine.MachineCode} accent={checked ? t.blue : null} t={t} isDark={isDark} checked={checked}>
                          <FormControlLabel
                            control={<Checkbox checked={checked} onChange={() => toggleSelectedMachine(machine.MachineCode)} size="small"
                              sx={{ color: t.muted, "&.Mui-checked": { color: t.blue } }} />}
                            label={
                              <Box sx={{ ml: 0.5 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.heading }}>
                                  {machine.MachineDescription}{" "}
                                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: t.blue }}>({machine.MachineCode})</span>
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: t.secondary, mt: 0.3 }}>
                                  Group: {machine.MachineGroupCode} · Availability: {machine.avaliability} · Active User: {machine.activeUserEmpId || "—"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2 }}>{machine.reason}</Typography>
                              </Box>
                            }
                          />
                        </ItemCard>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <Divider />

              {/* Open Detail Inconsistencies */}
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
                  <SectionHeading label="Open Detail Inconsistencies" t={t} />
                  <Button variant="contained" size="small" startIcon={<Build />}
                    onClick={fixSelectedOpenDetails} disabled={selectedDetailIds.length === 0}
                    sx={dangerBtnSx}
                  >
                    Fix Selected
                  </Button>
                </Stack>
                {stuckData.orphanOpenDetails.length === 0 ? (
                  <Alert severity="success" sx={{ borderRadius: "10px" }}>No open detail inconsistencies found.</Alert>
                ) : (
                  <Stack spacing={1}>
                    {stuckData.orphanOpenDetails.map((detail) => {
                      const checked = selectedDetailIds.includes(detail.detail_log_id);
                      return (
                        <ItemCard key={detail.detail_log_id} accent={checked ? t.red : null} t={t} isDark={isDark} checked={checked}>
                          <FormControlLabel
                            control={<Checkbox checked={checked} onChange={() => toggleSelectedDetail(detail.detail_log_id)} size="small"
                              sx={{ color: t.muted, "&.Mui-checked": { color: t.red } }} />}
                            label={
                              <Box sx={{ ml: 0.5 }}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" mb={0.5}>
                                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.heading }}>
                                    Detail ID:{" "}
                                    <span style={{ fontFamily: "monospace", color: t.blue }}>{detail.detail_log_id}</span>
                                  </Typography>
                                  <Chip size="small" color="error" label={detail.operation_code || "JOB_STARTED"} />
                                  <Chip size="small" label={detail.station_code} variant="outlined" />
                                </Stack>
                                <Typography sx={{ fontSize: 11, color: t.secondary }}>
                                  Emp: {detail.emp_name} ({detail.emp_id}) · Job: {detail.job_number || "—"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: t.secondary }}>
                                  Start: {formatDateTime(detail.start_datetime)} · Recommended End: {formatDateTime(detail.recommended_end_datetime)}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2 }}>{detail.reason}</Typography>
                              </Box>
                            }
                          />
                        </ItemCard>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Confirm dialog */}
      <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: 15, fontWeight: 700, color: t.heading, borderBottom: `0.5px solid ${t.border}`, px: 2.5, py: 2 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: "8px", flexShrink: 0, background: t.amberAlpha, border: `0.5px solid ${t.amber}35`, display: "flex", alignItems: "center", justifyContent: "center", color: isDark ? "#fbbf24" : "#d97706" }}>
            <WarningAmber sx={{ fontSize: 17 }} />
          </Box>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px", fontSize: 13 }}>{confirmDialog.message}</Alert>
          <TextField fullWidth multiline minRows={3} label="Reason / Remarks" value={reason}
            onChange={(e) => setReason(e.target.value)} placeholder="Enter cleanup reason…" />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, borderTop: `0.5px solid ${t.border}`, gap: 1 }}>
          <Button onClick={closeConfirm} disabled={actionLoading} color="inherit" sx={{ fontWeight: 600, fontSize: 13 }}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmAction} disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={15} color="inherit" /> : null}
            sx={dangerBtnSx}>
            {actionLoading ? "Processing…" : "Confirm Action"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManhourSessionRecoveryPanel;